import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import { Menu, X, ChevronDown } from 'lucide-react';

const Navbar = () => {
  const { language, toggleLanguage, t } = useLanguage();
  const [isScrolled, setIsScrolled]       = useState(false);
  const [isMobileOpen, setIsMobileOpen]   = useState(false);
  const [mobileExpanded, setMobileExpanded] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const navRef = useRef(null);

  const navLinks = [
    { path: '/', label: t('nav.home') },
    {
      path: '/ecosystem',
      label: t('nav.ecosystem'),
      dropdown: [
        { path: '/ecosystem?tab=products',     label: t('nav.dropdown.products')     },
        { path: '/ecosystem?tab=services',     label: t('nav.dropdown.services')     },
        { path: '/ecosystem?tab=applications', label: t('nav.dropdown.applications') },
        { path: '/ecosystem?tab=platform',     label: t('nav.dropdown.platform')     },
      ],
    },
    {
      path: '/about',
      label: t('nav.about'),
      dropdown: [
        { path: '/about#company',    label: t('nav.dropdown.company')    },
        { path: '/about#team',       label: t('nav.dropdown.team')       },
        { path: '/about#facilities', label: t('nav.dropdown.facilities') },
        { path: '/about#partners',   label: t('nav.dropdown.partners')   },
      ],
    },
    { path: 'https://takumimotioncontrols.zohorecruit.in/jobs/Careers', label: 'Careers', external: true },
    { path: '/contact', label: t('nav.contact') },
  ];

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 50);
    window.addEventListener('scroll', onScroll);
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  useEffect(() => {
    setIsMobileOpen(false);
    setMobileExpanded(null);
  }, [location]);

  /* Close menu when user clicks/taps outside the navbar */
  useEffect(() => {
    if (!isMobileOpen) return;
    const handleOutsideClick = (e) => {
      if (navRef.current && !navRef.current.contains(e.target)) {
        setIsMobileOpen(false);
        setMobileExpanded(null);
      }
    };
    document.addEventListener('mousedown', handleOutsideClick);
    document.addEventListener('touchstart', handleOutsideClick, { passive: true });
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
      document.removeEventListener('touchstart', handleOutsideClick);
    };
  }, [isMobileOpen]);

  const handleDropdownClick = (e, path) => {
    const [pagePath, hash] = path.split('#');
    if (!hash) return;
    e.preventDefault();
    if (location.pathname === pagePath) {
      document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(pagePath);
      setTimeout(() => {
        document.getElementById(hash)?.scrollIntoView({ behavior: 'smooth' });
      }, 300);
    }
  };

  const isActive = (path) =>
    path === '/' ? location.pathname === '/' : location.pathname.startsWith(path.split('?')[0]);

  return (
    <nav
      ref={navRef}
      data-testid="navbar"
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white ${
        isScrolled ? 'shadow-sm' : 'border-b border-[#D2D2D2]'
      }`}
    >
      <div className="max-w-[1600px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-[88px] lg:h-[64px]">

          {/* ── Logo ── */}
          <Link to="/" data-testid="logo-link" className="flex items-center shrink-0" onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}>
            <img
              src="/Logotype.png"
              alt="Takumi Motion Controls"
              className="h-[92px] lg:h-[105px] object-contain object-left self-center -ml-[18px] lg:-ml-4"
            />
          </Link>

          {/* ── Desktop nav + language ── */}
          <div className="hidden lg:flex items-center gap-10">
            {navLinks.map((link) => (
              <div key={link.path} className="relative group">
                {link.external ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid={`nav-link-${link.label.toLowerCase()}`}
                    className="flex items-center gap-1 py-2 text-[17px] font-medium transition-colors duration-200 text-[#0F0F0F] hover:text-[#D64545]"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    data-testid={`nav-link-${link.path.replace('/', '') || 'home'}`}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex items-center gap-1 py-2 text-[17px] font-medium transition-colors duration-200 ${
                      isActive(link.path) ? 'text-[#D64545]' : 'text-[#0F0F0F] hover:text-[#D64545]'
                    }`}
                  >
                    {link.label}
                    {link.dropdown && (
                      <ChevronDown className="w-3.5 h-3.5 transition-transform duration-200 group-hover:rotate-180" />
                    )}
                  </Link>
                )}

                {/* Dropdown — CSS-only hover, no JS timing issues */}
                {link.dropdown && (
                  <div className="absolute left-0 top-full pt-[6px] opacity-0 -translate-y-2 pointer-events-none transition-all duration-200 group-hover:opacity-100 group-hover:translate-y-0 group-hover:pointer-events-auto">
                    <div className="min-w-[220px] bg-[#D64545] overflow-hidden shadow-lg">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.path}
                          to={item.path}
                          onClick={(e) => handleDropdownClick(e, item.path)}
                          className="block px-5 py-[13px] text-[14px] font-medium text-white hover:bg-[#c23b3b] transition-colors duration-150"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            ))}

            {/* Language toggle */}
            <button
              onClick={toggleLanguage}
              data-testid="language-toggle"
              className="shrink-0 focus:outline-none"
              aria-label="Toggle language"
            >
              <img
                src={language === 'en' ? '/EN-Lang.png' : '/JP-Lang.png'}
                alt={language === 'en' ? 'EN' : 'JP'}
                className="h-9 object-contain"
              />
            </button>
          </div>

          {/* ── Mobile menu button ── */}
          <button
            onClick={() => setIsMobileOpen(!isMobileOpen)}
            data-testid="mobile-menu-toggle"
            className="lg:hidden p-2 text-[#0F0F0F] hover:text-[#D64545] transition-colors"
            aria-label="Toggle mobile menu"
          >
            {isMobileOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>
      </div>

      {/* ── Mobile menu ── */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-300 bg-white border-t border-[#D2D2D2] ${
          isMobileOpen ? 'max-h-[600px]' : 'max-h-0'
        }`}
      >
        <div className="px-6 py-4 space-y-1">
          {navLinks.map((link) => (
            <div key={link.path}>
              <div className="flex items-center justify-between">
                {link.external ? (
                  <a
                    href={link.path}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex-1 block py-2.5 text-[15px] font-medium transition-colors text-[#4A4A4A]"
                  >
                    {link.label}
                  </a>
                ) : (
                  <Link
                    to={link.path}
                    onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                    className={`flex-1 block py-2.5 text-[15px] font-medium transition-colors ${
                      isActive(link.path) ? 'text-[#D64545]' : 'text-[#4A4A4A]'
                    }`}
                  >
                    {link.label}
                  </Link>
                )}
                {link.dropdown && (
                  <button
                    onClick={() => setMobileExpanded(mobileExpanded === link.path ? null : link.path)}
                    className="p-1 text-[#4A4A4A]"
                  >
                    <ChevronDown
                      className={`w-4 h-4 transition-transform ${
                        mobileExpanded === link.path ? 'rotate-180' : ''
                      }`}
                    />
                  </button>
                )}
              </div>
              {link.dropdown && mobileExpanded === link.path && (
                <div className="ml-4 mb-2 bg-[#D64545] overflow-hidden">
                  {link.dropdown.map((item) => (
                    <Link
                      key={item.path}
                      to={item.path}
                      className="block px-4 py-2.5 text-[13px] font-medium text-white hover:bg-[#c23b3b] transition-colors"
                    >
                      {item.label}
                    </Link>
                  ))}
                </div>
              )}
            </div>
          ))}
          <div className="pt-3 border-t border-[#D2D2D2]">
            <button
              onClick={toggleLanguage}
              className="shrink-0 focus:outline-none"
              aria-label="Toggle language"
            >
              <img
                src={language === 'en' ? '/EN-Lang.png' : '/JP-Lang.png'}
                alt={language === 'en' ? 'EN' : 'JP'}
                className="h-9 object-contain"
              />
            </button>
          </div>
        </div>
      </div>
    </nav>
  );
};

export default Navbar;
